import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useTranslations } from 'use-intl'
import {
  ArrowRight,
  BellRing,
  ClipboardCheck,
  FileText,
  Menu,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { BookDemoButton, ClientAreaButton } from '@/components/cta-buttons'
import { CATEGORY_ICONS } from '@/components/product-category'
import { Logo } from '@/components/ui/logo'
import { LocaleSwitcher } from '@/components/locale-switcher'
import type { FooterPost } from '@/components/layout/footer'
import { SOLUTIONS } from '@/lib/solutions'
import { APP_ASSETS } from '@/features/landing-page/data/app-detail'
import { APP_NAMES } from '@/features/solution-page/data/app-showcase'
import { FEATURE_GROUPS } from '@/features/solution-page/data/features'
import { getNavResourceGroups } from '@/features/resources/data/resources'
import { trackLeadMagnetClick, trackNavClick } from '@/lib/analytics/events'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'

// ponytail: pricing page hidden from nav — const PRICING_TO = '/$locale/pricing'
// ponytail: temporary tool→route mapping (marketing labels ≠ actual page titles)
const FREE_TOOLS = [
  { key: 'solution', to: '/$locale/tools/solution-configurator', icon: SlidersHorizontal },
  { key: 'compliance', to: '/$locale/tools/compliance-audit', icon: ShieldCheck },
  { key: 'ppms', to: '/$locale/tools/ppms-audit', icon: ClipboardCheck },
  { key: 'maturity', to: '/$locale/tools/security-audit-alerts', icon: BellRing },
] as const

const CONFIGURATOR_TO = '/$locale/tools/solution-configurator'
const RESOURCE_TO = '/$locale/resources/$slug'
const BLOG_TO = '/$locale/blog'
const POST_TO = '/$locale/blog/$slug'
// Same 2 posts on desktop and mobile; the layout loader fetches them once for
// the header and the footer.
const MENU_POSTS = 2

export function Header({ posts }: { posts: readonly FooterPost[] }) {
  const t = useTranslations()
  const { locale } = useParams({ from: '/$locale' })
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const close = () => setOpen(false)
  const latestPosts = posts.slice(0, MENU_POSTS)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = () => mq.matches && setOpen(false)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return (
    <header className="sticky top-0 z-40 px-4 pt-2 sm:px-6">
      {/* `relative`: the desktop menu panel spans this bar's full width. */}
      <div
        className={cn(
          'relative mx-auto flex max-w-7xl items-center justify-between rounded-2xl border pl-2 py-4 transition-all duration-300 lg:grid lg:grid-cols-[minmax(max-content,1fr)_auto_minmax(max-content,1.3fr)] lg:px-8',
          scrolled
            ? 'border-white/20 bg-background/60 backdrop-blur-xl backdrop-saturate-150'
            : 'border-transparent bg-transparent',
        )}
      >
        <div className="flex items-center gap-2">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-10 lg:hidden"
                aria-label="Menu"
              >
                <Menu className="size-7" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-full gap-0 border-r-0 bg-background p-0 sm:max-w-md [&>button]:hidden"
            >
              <SheetTitle className="sr-only">Menu</SheetTitle>

              <div className="flex items-center justify-between px-5 pt-6 pb-2">
                <Logo size="md" alt="CountAct" className="h-5 md:h-6" />
                <SheetClose asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="flex size-10 items-center justify-center rounded-full border border-muted bg-white text-primary shadow-sm"
                  >
                    <X className="size-5" />
                  </button>
                </SheetClose>
              </div>

              <div className="flex-1 overflow-y-auto px-5">
                <Accordion type="single" collapsible>
                  <MobileSection value="solutions" title={t('nav.solutions')}>
                    {FEATURE_GROUPS.map((group) => (
                      <div key={group.key}>
                        <MobileGroupLink
                          to={SOLUTIONS[group.key].to}
                          locale={locale}
                          label={t(`nav.solutionsItems.${group.key}`)}
                          action={t('nav.see')}
                          onNavigate={close}
                        />
                        <ul>
                          {group.features.map((feature) => (
                            <MobileRow
                              key={feature}
                              to={group.to}
                              params={{ locale, feature }}
                              icon={<AppIcon src={APP_ASSETS[feature].colorIcon} />}
                              label={APP_NAMES[feature]}
                              onNavigate={close}
                            />
                          ))}
                        </ul>
                      </div>
                    ))}
                    <Link
                      to={CONFIGURATOR_TO as never}
                      params={{ locale } as never}
                      onClick={() => {
                        trackNavClick({ destination: CONFIGURATOR_TO, source: 'header' })
                        close()
                      }}
                      className="mt-3 block rounded-xl bg-muted-hover px-4 py-3.5"
                    >
                      <span className="block text-xs text-muted-foreground">
                        {t('nav.featured')}
                      </span>
                      <span className="mt-1 block font-serif text-base font-semibold text-foreground">
                        {t('nav.featuredTool.title')}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1 text-sm text-secondary-foreground">
                        {t('nav.featuredTool.short')}
                        <ArrowRight className="size-3.5 text-primary" />
                      </span>
                    </Link>
                  </MobileSection>

                  <MobileSection value="resources" title={t('nav.resources')}>
                    {getNavResourceGroups().map((group) => (
                      <div key={group.key}>
                        <MobileGroupHeading>
                          {t(`nav.resourcesGroups.${group.key}`)}
                        </MobileGroupHeading>
                        <ul>
                          {group.resources.map((r) => (
                            <MobileRow
                              key={r.slug}
                              to={RESOURCE_TO}
                              params={{ locale, slug: r.slug }}
                              icon={<FileText className="size-5 text-primary" />}
                              label={t(`nav.resourcesItems.${r.slug}`)}
                              onNavigate={() => {
                                trackLeadMagnetClick({ magnetId: r.slug, pageType: 'resource_index' })
                                close()
                              }}
                            />
                          ))}
                        </ul>
                      </div>
                    ))}
                    <MobileGroupHeading>{t('nav.fromBlog')}</MobileGroupHeading>
                    <ul>
                      {latestPosts.map((post) => (
                        <li key={post.slug}>
                          <Link
                            to={POST_TO as never}
                            params={{ locale, slug: post.slug } as never}
                            onClick={() => {
                              trackNavClick({ destination: POST_TO, source: 'header' })
                              close()
                            }}
                            className="line-clamp-2 py-2 text-sm font-medium text-foreground"
                          >
                            {post.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={BLOG_TO as never}
                      params={{ locale } as never}
                      onClick={() => {
                        trackNavClick({ destination: BLOG_TO, source: 'header' })
                        close()
                      }}
                      className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary"
                    >
                      {t('nav.allPosts')}
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </MobileSection>

                  <MobileSection value="tools" title={t('nav.freeTools')}>
                    <ul className="pt-2">
                      {FREE_TOOLS.map((ft) => (
                        <MobileRow
                          key={ft.key}
                          to={ft.to}
                          params={{ locale }}
                          icon={<ft.icon className="size-5 text-primary" />}
                          label={t(`nav.toolsItems.${ft.key}`)}
                          onNavigate={close}
                        />
                      ))}
                    </ul>
                  </MobileSection>

                  {/* ponytail: pricing page hidden from nav
                  <Link to={PRICING_TO} …>{t('nav.pricing')}</Link> */}
                </Accordion>
              </div>

              {/* `onClick={close}` closes the sheet when the CTA navigates or
                  scrolls; the trial form dialog overrides it and opens on top. */}
              <div className="grid gap-2 border-t border-muted p-5">
                <BookDemoButton source="header" size="xl" className="w-full py-0" onClick={close} />
                <ClientAreaButton size="xl" className="w-full py-0" />
              </div>
            </SheetContent>
          </Sheet>

          <Link
            to="/$locale"
            params={{ locale } as never}
            aria-label="CountAct"
            onClick={() => trackNavClick({ destination: '/$locale', source: 'header' })}
          >
            <Logo
              size="xs"
              alt="CountAct"
              className="h-[22px] sm:h-[22px] md:h-[22px] lg:h-5"
            />
          </Link>
        </div>

        <div className="hidden lg:flex lg:justify-self-center lg:mx-4 xl:mx-8">
          {/* One shared panel under the whole bar (`static` root + inset-x-0
              wrapper): every menu opens in the same place and the panel
              resizes between them instead of jumping under each trigger. */}
          <NavigationMenu
            className="static max-w-none"
            viewportWrapperClassName="inset-x-0 lg:px-0"
            viewportClassName="border-border bg-white shadow-lg shadow-primary/5 backdrop-blur-none md:w-full"
          >
            <NavigationMenuList className="gap-1 xl:gap-6">
              <NavigationMenuItem>
                <NavigationMenuTrigger>
                  {t('nav.solutions')}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="p-0 pr-0 md:w-full">
                  <div className="grid xl:grid-cols-[minmax(0,2fr)_minmax(0,0.85fr)]">
                    <div className="p-4">
                      {FEATURE_GROUPS.map((group, i) => (
                        <div
                          key={group.key}
                          className={cn(i > 0 && 'mt-3 border-t border-muted pt-3')}
                        >
                          <MenuGroupLink
                            to={SOLUTIONS[group.key].to}
                            locale={locale}
                            icon={CATEGORY_ICONS[group.key]}
                            label={t(`nav.solutionsItems.${group.key}`)}
                            action={t('nav.seeSolution')}
                          />
                          <ul className="grid grid-cols-2 gap-1">
                            {group.features.map((feature) => (
                              <MenuLink
                                key={feature}
                                to={group.to}
                                params={{ locale, feature }}
                                icon={<AppIcon src={APP_ASSETS[feature].colorIcon} />}
                                title={APP_NAMES[feature]}
                                description={t(
                                  `solutions.${group.key}.appShowcase.cards.${feature}.desc`,
                                )}
                              />
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    {/* The configurator is also under "Outils gratuits", so
                        the featured card can drop where it doesn't fit. */}
                    <MenuAside label={t('nav.featured')} className="hidden xl:block">
                      <NavigationMenuLink asChild>
                        <Link
                          to={CONFIGURATOR_TO as never}
                          params={{ locale } as never}
                          onClick={() =>
                            trackNavClick({ destination: CONFIGURATOR_TO, source: 'header' })
                          }
                          className="group/feat !block overflow-hidden !rounded-2xl border border-muted bg-white !p-0 shadow-sm transition-shadow hover:!bg-white hover:shadow-md focus:!bg-white"
                        >
                          <span className="bg-app-highlight flex h-36 items-end justify-center overflow-hidden">
                            <img
                              src="/mascot/mascot-free-tool.webp"
                              alt=""
                              aria-hidden
                              width={853}
                              height={650}
                              loading="lazy"
                              decoding="async"
                              className="h-32 w-auto transition-transform duration-300 group-hover/feat:scale-[1.03]"
                            />
                          </span>
                          <span className="block p-3.5">
                            <span className="block font-serif text-base font-semibold text-foreground">
                              {t('nav.featuredTool.title')}
                            </span>
                            <span className="mt-1 block text-xs leading-snug text-secondary-foreground">
                              {t('nav.featuredTool.desc')}
                            </span>
                            <span className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
                              {t('nav.featuredTool.cta')}
                              <ArrowRight className="size-3.5 text-primary-foreground transition-transform group-hover/feat:translate-x-0.5" />
                            </span>
                          </span>
                        </Link>
                      </NavigationMenuLink>
                    </MenuAside>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger>
                  {t('nav.resources')}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="p-0 pr-0 md:w-full">
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.95fr)]">
                    {getNavResourceGroups().map((group) => (
                      <div key={group.key} className="p-4">
                        <MenuGroupHeading>
                          {t(`nav.resourcesGroups.${group.key}`)}
                        </MenuGroupHeading>
                        <ul className="grid gap-0.5">
                          {group.resources.map((r) => (
                            <MenuLink
                              key={r.slug}
                              to={RESOURCE_TO}
                              params={{ locale, slug: r.slug }}
                              icon={<FileText className="size-5 text-primary" />}
                              title={t(`nav.resourcesItems.${r.slug}`)}
                              description={t(`nav.resourcesDesc.${r.slug}`)}
                              onClick={() =>
                                trackLeadMagnetClick({ magnetId: r.slug, pageType: 'resource_index' })
                              }
                            />
                          ))}
                        </ul>
                      </div>
                    ))}
                    <MenuAside label={t('nav.fromBlog')}>
                      <ul className="grid gap-2">
                        {latestPosts.map((post) => (
                          <li key={post.slug}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={POST_TO as never}
                                params={{ locale, slug: post.slug } as never}
                                onClick={() =>
                                  trackNavClick({ destination: POST_TO, source: 'header' })
                                }
                                className="group/post !flex-row items-center !gap-3 !rounded-xl border border-muted bg-white !p-2 transition-colors hover:!bg-white hover:border-border focus:!bg-white"
                              >
                                <PostThumb />
                                <span className="min-w-0">
                                  <span className="line-clamp-2 text-xs leading-snug font-semibold text-foreground group-hover/post:text-primary">
                                    {post.title}
                                  </span>
                                </span>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                      <NavigationMenuLink asChild>
                        <Link
                          to={BLOG_TO as never}
                          params={{ locale } as never}
                          onClick={() => trackNavClick({ destination: BLOG_TO, source: 'header' })}
                          className="mt-2 !inline-flex !flex-row items-center !gap-1 !p-1 text-xs font-semibold text-primary hover:!bg-transparent hover:!text-primary focus:!bg-transparent"
                        >
                          {t('nav.allPosts')}
                          <ArrowRight className="size-3.5 text-primary" />
                        </Link>
                      </NavigationMenuLink>
                    </MenuAside>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger>
                  {t('nav.freeTools')}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="p-0 pr-0 md:w-full">
                  <ul className="grid grid-cols-2 gap-1 p-4">
                    {FREE_TOOLS.map((ft) => (
                      <MenuLink
                        key={ft.key}
                        to={ft.to}
                        params={{ locale }}
                        icon={<ft.icon className="size-5 text-primary" />}
                        title={t(`nav.toolsItems.${ft.key}`)}
                        description={t(`nav.toolsDesc.${ft.key}`)}
                      />
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* ponytail: pricing page hidden from nav
              <NavigationMenuItem>
                <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                  <Link to={PRICING_TO as never} params={{ locale } as never}>
                    {t('nav.pricing')}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem> */}

            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="flex items-center gap-2 lg:justify-self-end lg:justify-end">
          {/* Stretched to the row: its hover pill matches the CTAs' height. */}
          <LocaleSwitcher className="self-stretch text-primary" />
          <div className="hidden lg:contents">
            <ClientAreaButton />
            <BookDemoButton source="header" />
          </div>
        </div>
      </div>
    </header>
  )
}

/** Lavender tile holding an app icon or a Lucide glyph, shared by every row. */
function IconTile({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'bg-app-highlight flex size-10 shrink-0 items-center justify-center rounded-lg border border-muted',
        className,
      )}
    >
      {children}
    </span>
  )
}

function AppIcon({ src }: { src: string }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      width={128}
      height={128}
      loading="lazy"
      decoding="async"
      className="size-5"
    />
  )
}

/** Blog-card cover in miniature: the shared 3d-blue background + white logo. */
function PostThumb() {
  return (
    <span className="relative isolate flex h-12 w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-lg">
      <img
        src="/backgrounds/abstract-3d-blue-2.webp"
        alt=""
        aria-hidden
        width={759}
        height={426}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 -z-10 size-full object-cover"
      />
      <Logo size="xs" alt="" aria-hidden className="h-2 brightness-0 invert lg:h-2" />
    </span>
  )
}

function MenuGroupHeading({ children }: { children: ReactNode }) {
  return <p className="mb-1 px-2.5 text-xs font-medium text-muted-foreground">{children}</p>
}

/** Tinted right-hand column ("À la une", "Du blog"). */
function MenuAside({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('border-l border-muted bg-gradient-to-br from-muted-hover to-muted/50 p-4', className)}>
      <p className="mb-2 px-1 text-xs font-medium text-muted-foreground">{label}</p>
      {children}
    </div>
  )
}

/** Solution heading: the category name and "Voir la solution" as one link. */
function MenuGroupLink({
  to,
  locale,
  icon,
  label,
  action,
}: {
  to: string
  locale: string
  icon: string
  label: string
  action: string
}) {
  return (
    <NavigationMenuLink asChild>
      <Link
        to={to as never}
        params={{ locale } as never}
        onClick={() => trackNavClick({ destination: to, source: 'header' })}
        className="group/head mb-1 !flex-row items-center justify-between !gap-3 !rounded-lg !px-2.5 !py-1.5 hover:!bg-transparent focus:!bg-muted-hover"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <img src={icon} alt="" aria-hidden width={37} height={37} className="size-4" />
          {label}
        </span>
        <span className="flex items-center gap-1 text-xs font-semibold text-primary">
          {action}
          <ArrowRight className="size-3.5 text-primary transition-transform group-hover/head:translate-x-0.5" />
        </span>
      </Link>
    </NavigationMenuLink>
  )
}

/** Desktop menu row: icon tile, title, optional one-line description. */
function MenuLink({
  to,
  params,
  icon,
  title,
  description,
  onClick,
}: {
  to: string
  params: Record<string, string>
  icon: ReactNode
  title: string
  description?: string
  onClick?: () => void
}) {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={to as never}
          params={params as never}
          onClick={() => {
            trackNavClick({ destination: to, source: 'header' })
            onClick?.()
          }}
          className={cn(
            'group/item h-full !flex-row !gap-3 !rounded-xl !p-2.5 no-underline outline-none transition-colors hover:!bg-muted-hover focus:!bg-muted-hover',
            description ? 'items-start' : 'items-center',
          )}
        >
          <IconTile>{icon}</IconTile>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-semibold text-foreground transition-colors group-hover/item:text-primary">
              {title}
            </span>
            {description && (
              <span className="text-xs leading-snug text-secondary-foreground">
                {description}
              </span>
            )}
          </span>
        </Link>
      </NavigationMenuLink>
    </li>
  )
}

function MobileSection({
  value,
  title,
  children,
}: {
  value: string
  title: string
  children: ReactNode
}) {
  return (
    <AccordionItem value={value} className="border-muted">
      <AccordionTrigger
        hideChevron
        className="group/acc px-0 py-5 font-serif text-xl font-semibold text-foreground hover:no-underline"
      >
        {title}
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-muted bg-white text-primary shadow-sm">
          <svg
            viewBox="0 0 24 24"
            aria-hidden
            className="size-4 fill-none stroke-current stroke-[2.5] transition-transform duration-200 group-data-[state=open]/acc:rotate-180"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </AccordionTrigger>
      <AccordionContent className="pb-5">{children}</AccordionContent>
    </AccordionItem>
  )
}

function MobileGroupHeading({ children }: { children: ReactNode }) {
  return <p className="mt-3 mb-1 text-xs text-muted-foreground">{children}</p>
}

/** Mobile solution heading: the category name and "Voir" as one link. */
function MobileGroupLink({
  to,
  locale,
  label,
  action,
  onNavigate,
}: {
  to: string
  locale: string
  label: string
  action: string
  onNavigate: () => void
}) {
  return (
    <Link
      to={to as never}
      params={{ locale } as never}
      onClick={() => {
        trackNavClick({ destination: to, source: 'header' })
        onNavigate()
      }}
      className="mt-3 mb-1 flex items-baseline justify-between text-xs"
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1 font-semibold text-primary">
        {action}
        <ArrowRight className="size-3.5" />
      </span>
    </Link>
  )
}

function MobileRow({
  to,
  params,
  icon,
  label,
  onNavigate,
}: {
  to: string
  params: Record<string, string>
  icon: ReactNode
  label: string
  onNavigate: () => void
}) {
  return (
    <li>
      <Link
        to={to as never}
        params={params as never}
        onClick={() => {
          trackNavClick({ destination: to, source: 'header' })
          onNavigate()
        }}
        className="flex items-center gap-3 rounded-lg py-1.5 text-sm font-medium text-foreground transition-colors hover:text-primary"
      >
        <IconTile className="size-9">{icon}</IconTile>
        {label}
      </Link>
    </li>
  )
}
